import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getSessions, createSession, updateSession, deleteSession } from './coachApi';

const SESSION_TYPES = [
  { id: 'stress',     icon: '🧘', nl: 'Stress & Angst',       en: 'Stress & Anxiety' },
  { id: 'goals',      icon: '🎯', nl: 'Doelen Stellen',       en: 'Goal Setting' },
  { id: 'motivation', icon: '⚡', nl: 'Motivatie Boost',      en: 'Motivation Boost' },
  { id: 'sleep',      icon: '😴', nl: 'Slaap Optimalisatie',  en: 'Sleep Optimization' },
  { id: 'growth',     icon: '🌱', nl: 'Persoonlijke Groei',   en: 'Personal Growth' },
  { id: 'emotional',  icon: '❤️', nl: 'Emotioneel Welzijn',  en: 'Emotional Wellbeing' },
  { id: 'burnout',    icon: '🔥', nl: 'Burn-out Check',       en: 'Burnout Check' },
  { id: 'precomp',    icon: '🏆', nl: 'Pre-Competitie',       en: 'Pre-Competition' },
  { id: 'debrief',    icon: '📋', nl: 'Post-Game Debrief',    en: 'Post-Game Debrief' },
  { id: 'custom',     icon: '💬', nl: 'Vrij gesprek',         en: 'Free Session' },
];

const STATUS_COLORS = { scheduled: '#C8A84B', completed: '#4AC87A', cancelled: '#C05050', inprogress: '#5B9ED0' };

const EMPTY_FORM = { client_id: '', client_name: '', type: 'stress', date: '', time: '10:00', duration: 60, notes: '', location: '', online_link: '' };

export default function CoachSessionsTab({ coachId, clients, lang }) {
  const nl = lang === 'nl';
  const [sessions, setSessions]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filter, setFilter]       = useState('upcoming'); // upcoming | past | all
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionNote, setSessionNote] = useState('');
  const [savingNote, setSavingNote]   = useState(false);

  const load = useCallback(() => {
    getSessions(coachId).then(d => { if (Array.isArray(d)) setSessions(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();

  const filtered = sessions.filter(s => {
    const d = new Date(`${s.date}T${s.time || '00:00'}`);
    if (filter === 'upcoming') return d >= now && s.status !== 'cancelled';
    if (filter === 'past')     return d < now  || s.status === 'completed';
    return true;
  }).sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));

  const handleSave = async () => {
    if (!form.date || !form.time) return;
    setSaving(true);
    const data = { ...form, status: 'scheduled', coach_id: coachId };
    if (editId) {
      await updateSession(coachId, editId, data).catch(() => {});
    } else {
      await createSession(coachId, data).catch(() => {});
    }
    setSaving(false);
    setShowNew(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    load();
  };

  const handleMarkDone = async (s) => {
    await updateSession(coachId, s.id, { ...s, status: 'completed', session_note: sessionNote }).catch(() => {});
    setSelectedSession(null);
    setSessionNote('');
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(nl ? 'Sessie verwijderen?' : 'Delete session?')) return;
    await deleteSession(coachId, id).catch(() => {});
    load();
  };

  const openEdit = (s) => {
    setForm({ client_id: s.client_id, client_name: s.client_name, type: s.type || 'stress', date: s.date, time: s.time, duration: s.duration || 60, notes: s.notes || '', location: s.location || '', online_link: s.online_link || '' });
    setEditId(s.id);
    setShowNew(true);
  };

  const typeOf = (id) => SESSION_TYPES.find(t => t.id === id) || SESSION_TYPES[SESSION_TYPES.length - 1];

  // ── Detail view ──────────────────────────────────────────────────────────────
  if (selectedSession) {
    const s  = selectedSession;
    const tc = typeOf(s.type);
    return (
      <div style={{ padding: '0 0 32px' }}>
        <button onClick={() => setSelectedSession(null)} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 16px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 18, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 32 }}>{tc.icon}</div>
            <div>
              <div style={{ color: C.white, fontSize: 16, fontWeight: 700 }}>{nl ? tc.nl : tc.en}</div>
              <div style={{ color: C.silver, fontSize: 12 }}>
                {s.client_name} · {new Date(s.date).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} {nl ? 'om' : 'at'} {s.time}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', background: `${STATUS_COLORS[s.status] || _T.accent}20`, color: STATUS_COLORS[s.status] || _T.accent, fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 20, textTransform: 'uppercase' }}>
              {s.status}
            </div>
          </div>
          {s.notes && <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>{s.notes}</div>}
          {s.location && <div style={{ color: C.silver2, fontSize: 12 }}>📍 {s.location}</div>}
          {s.online_link && (
            <a href={s.online_link} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: _T.accent, fontSize: 12, fontWeight: 600, marginTop: 8, textDecoration: 'none' }}>
              🔗 {nl ? 'Online sessie starten' : 'Start online session'}
            </a>
          )}
        </div>

        {s.status === 'scheduled' && (
          <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
              {nl ? 'Sessie afronden' : 'Complete session'}
            </div>
            <textarea value={sessionNote} onChange={e => setSessionNote(e.target.value)}
              placeholder={nl ? 'Sessienotities (optioneel)...' : 'Session notes (optional)...'}
              rows={4}
              style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', marginBottom: 10 }} />
            <button onClick={() => handleMarkDone(s)} disabled={savingNote}
              style={{ width: '100%', background: 'linear-gradient(135deg,#4AC87A,#2fa85f)', border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '12px', cursor: 'pointer' }}>
              ✓ {nl ? 'Markeer als voltooid' : 'Mark as completed'}
            </button>
          </div>
        )}

        {s.session_note && (
          <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, marginTop: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 8 }}>{nl ? 'SESSIENOTITIES' : 'SESSION NOTES'}</div>
            <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.6 }}>{s.session_note}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Sessies' : 'Sessions'}</div>
        <button onClick={() => { setShowNew(v => !v); setEditId(null); setForm(EMPTY_FORM); }}
          style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
          + {nl ? 'Plannen' : 'Schedule'}
        </button>
      </div>

      {/* New/edit form */}
      {showNew && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {editId ? (nl ? 'Sessie bewerken' : 'Edit session') : (nl ? 'Nieuwe sessie plannen' : 'Schedule new session')}
          </div>

          {/* Client picker */}
          <select value={form.client_id}
            onChange={e => { const c = clients?.find(x => x.user_id === e.target.value); setForm(f => ({ ...f, client_id: e.target.value, client_name: c?.name || '' })); }}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: form.client_id ? C.white : C.silver, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }}>
            <option value="">{nl ? 'Selecteer klant...' : 'Select client...'}</option>
            {(clients || []).map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
          </select>

          {/* Session type */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {SESSION_TYPES.map(t => (
              <button key={t.id} onClick={() => setForm(f => ({ ...f, type: t.id }))}
                style={{ padding: '5px 10px', borderRadius: 20, border: `1.5px solid ${form.type === t.id ? _T.accent : _T.cardBorderBot}`, background: form.type === t.id ? `${_T.accent}20` : _T.bg4, color: form.type === t.id ? _T.accent : C.silver, fontSize: 11, cursor: 'pointer' }}>
                {t.icon} {nl ? t.nl : t.en}
              </button>
            ))}
          </div>

          {/* Date + time */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
            <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
          </div>

          {/* Duration */}
          <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: Number(e.target.value) }))}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }}>
            {[30, 45, 60, 75, 90, 120].map(d => <option key={d} value={d}>{d} {nl ? 'minuten' : 'minutes'}</option>)}
          </select>

          {/* Location / link */}
          <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
            placeholder={nl ? '📍 Locatie (optioneel)' : '📍 Location (optional)'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          <input value={form.online_link} onChange={e => setForm(f => ({ ...f, online_link: e.target.value }))}
            placeholder="🔗 Zoom / Teams / Meet URL (optioneel)"
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />

          {/* Notes */}
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder={nl ? 'Aantekeningen / agenda (optioneel)' : 'Notes / agenda (optional)'}
            rows={3}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', marginBottom: 10 }} />

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setShowNew(false); setEditId(null); }}
              style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {nl ? 'Annuleren' : 'Cancel'}
            </button>
            <button onClick={handleSave} disabled={saving || !form.date || !form.time}
              style={{ flex: 1, background: form.date && form.time ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: form.date && form.time ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 13, padding: '10px', cursor: form.date && form.time ? 'pointer' : 'not-allowed' }}>
              {saving ? '...' : (editId ? (nl ? 'Opslaan' : 'Save') : (nl ? 'Inplannen' : 'Schedule'))}
            </button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[['upcoming', nl ? 'Aankomend' : 'Upcoming'], ['past', nl ? 'Voltooid' : 'Completed'], ['all', nl ? 'Alles' : 'All']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${filter === v ? _T.accent : _T.cardBorderBot}`, background: filter === v ? `${_T.accent}20` : _T.bg4, color: filter === v ? _T.accent : C.silver, fontSize: 12, fontWeight: filter === v ? 700 : 400, cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
          <div style={{ fontSize: 14 }}>{nl ? 'Geen sessies gevonden' : 'No sessions found'}</div>
        </div>
      ) : (
        filtered.map(s => {
          const tc    = typeOf(s.type);
          const d     = new Date(`${s.date}T${s.time || '00:00'}`);
          const isPast = d < now;
          return (
            <div key={s.id} onClick={() => setSelectedSession(s)}
              style={{ background: _T.bg3, border: `1px solid ${isPast && s.status !== 'completed' ? _T.cardBorderBot : s.status === 'completed' ? '#4AC87A33' : `${_T.accent}33`}`, borderRadius: 14, padding: 14, marginBottom: 10, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ fontSize: 26, flexShrink: 0 }}>{tc.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{s.client_name || (nl ? 'Klant' : 'Client')}</div>
                      <div style={{ color: C.silver, fontSize: 12 }}>{nl ? tc.nl : tc.en}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ color: STATUS_COLORS[s.status] || _T.accent, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{s.status}</div>
                      <div style={{ color: C.silver, fontSize: 11 }}>{d.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short' })} {s.time}</div>
                    </div>
                  </div>
                  <div style={{ color: C.silver2, fontSize: 11, marginTop: 4 }}>
                    {s.duration}min{s.location ? ` · ${s.location}` : s.online_link ? ' · Online' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <button onClick={e => { e.stopPropagation(); openEdit(s); }}
                    style={{ background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 7, color: C.silver, fontSize: 10, padding: '3px 7px', cursor: 'pointer' }}>✏️</button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                    style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer', padding: 0 }}>×</button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
